/* ============================================================================
   domain_selector — the field widget surface (foundation.presentation Enh 13).

     <field name="domain" widget="domain_selector" options-model="model_id | 'crm.lead'"/>

   · Read mode  → a compact chip: filter glyph + "<n> conditions on <model>".
   · Edit mode  → a clickable widget box that opens <DomainBuilderDialog/>; on
                  apply the serialized EDE domain is written back via onWriteField.

   The stored value is an EDE domain. We accept it as a JSON string (text/char
   field) OR an already-parsed array, and write it back in the SAME shape.
   Slice 1 resolves the target model statically (literal key, sibling key in the
   static spec, else "crm.lead"); Slice 2 wires real `options-model` DSL
   resolution + a live field-spec read.
   ========================================================================== */
import { useMemo, useState } from "react";
import { Filter } from "lucide-react";
import type { Json, RecordData } from "../../core";
import { useRuntime } from "../../react/runtime/RuntimeContext";
import type { ListCellContext } from "../types";
import type { Domain } from "./types";
import type { ModelSpec, SpecGraph } from "./fieldspec";
import { DomainBuilderDialog, EditIcon, type SpecLoader } from "./DomainBuilderDialog";

// Properties an m2o reference value might expose that carry a dotted model key
// (e.g. a record rule's `model_id` → `ir.model` whose key is the model).
const KEY_PROPS = ["key", "model", "model_key", "technical_name"] as const;

/** Extract a dotted model key from a sibling field's value, or null.
 *  An M2O chip carries `{id, display_name, data?}`; `ir.model` ships
 *  `data.model_key` (via `display_data_fields=["model_key"]`) so the key is
 *  read directly from the chosen value without parsing the display label. */
function extractModelKey(value: unknown): string | null {
    if (typeof value === "string") return value.includes(".") ? value : value || null;
    if (value && typeof value === "object") {
        const obj = value as Record<string, unknown>;
        // M2O chip `data` payload (preferred — carries the technical key).
        const data = obj.data;
        if (data && typeof data === "object") {
            for (const p of KEY_PROPS) {
                const v = (data as Record<string, unknown>)[p];
                if (typeof v === "string" && v.includes(".")) return v;
            }
        }
        for (const p of KEY_PROPS) {
            const v = obj[p];
            if (typeof v === "string" && v.includes(".")) return v;
        }
        // Fall back to a display field only when it itself looks like a key.
        const disp = obj.display_name ?? obj.name;
        if (typeof disp === "string" && disp.includes(".")) return disp;
    }
    return null;
}

/**
 * Resolve the model the domain filters on from the widget's `option-model`:
 *   · quoted literal — `option-model="'crm.lead'"` → `crm.lead`
 *   · sibling field  — `option-model="model_id"`   → the model key carried by
 *     the draft's `model_id` value (re-resolves as the sibling changes)
 *   · bare dotted key with no matching sibling → treated as a literal
 * Returns null when nothing resolves (widget renders disabled — "pick a model").
 */
export function resolveTargetModel(widgetOptions: Record<string, string> | null, draft: RecordData | undefined): string | null {
    const raw = widgetOptions?.model?.trim();
    if (!raw) return null;
    // Quoted literal always wins.
    if (/^(['"]).*\1$/.test(raw)) {
        const lit = raw.slice(1, -1).trim();
        return lit || null;
    }
    // Bare token → sibling field name first.
    const sib = draft?.[raw];
    if (sib !== undefined && sib !== null && sib !== "") {
        return extractModelKey(sib);
    }
    // Sibling unset: a bare dotted token is a literal key; otherwise no model yet.
    return raw.includes(".") ? raw : null;
}

/** Parse a stored value into a Domain; never throws (bad input → empty). */
export function coerceDomain(value: unknown): Domain {
    if (Array.isArray(value)) return value as Domain;
    if (typeof value === "string" && value.trim()) {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return parsed as Domain;
        } catch {
            /* fall through to empty */
        }
    }
    return [];
}

/** Count leaf conditions in a raw domain (array operands; ignores operators). */
export function leafCount(domain: Domain): number {
    return domain.filter((t) => Array.isArray(t)).length;
}

function summaryText(domain: Domain): string {
    const n = leafCount(domain);
    return n ? `${n} condition${n === 1 ? "" : "s"}` : "no filter — all records";
}

/* ---- read-mode renderer (registry `render`) --------------------------- */

export function DomainSelectorRead(ctx: ListCellContext): React.ReactNode {
    const domain = coerceDomain(ctx.value as unknown);
    const n = leafCount(domain);
    if (!n) return <span className="cell-muted">—</span>;
    return (
        <span className="domain-chip">
            <Filter size={12} />
            <span>{n} condition{n === 1 ? "" : "s"}</span>
        </span>
    );
}

/* ---- edit-mode component (mounted from FormSheet FieldEditor) --------- */

export interface DomainSelectorEditorProps {
    name: string;
    value: unknown;
    draft: RecordData;
    widgetOptions: Record<string, string> | null;
    onWriteField: (name: string, value: Json) => void;
    /** Async field-spec resolver — wired by the form host to the live
     *  introspection read. When omitted the builder opens with whatever
     *  `spec` provides (tests inject a static fetcher / graph). */
    loadSpec?: SpecLoader;
    /** Pre-seeded spec graph (static / test path). */
    spec?: SpecGraph;
    /** Async match-count resolver for the builder footer (Scope E). */
    countFor?: (model: string, domain: Domain) => Promise<number | null>;
    /** Async option resolver for relational value chips (Scope C). */
    loadOptions?: (relModel: string) => Promise<Array<[string, string]>>;
}

export function DomainSelectorEditor({ name, value, draft, widgetOptions, onWriteField, loadSpec, spec, countFor, loadOptions }: DomainSelectorEditorProps) {
    const [open, setOpen] = useState(false);
    const model = resolveTargetModel(widgetOptions, draft);
    const domain = coerceDomain(value);
    const storedAsString = typeof value === "string" || value == null;

    const handleApply = (next: Domain) => {
        const out: Json = storedAsString ? (JSON.stringify(next) as Json) : (next as unknown as Json);
        onWriteField(name, out);
        setOpen(false);
    };

    // No resolvable target model → disabled affordance (e.g. a record rule whose
    // model_id sibling isn't picked yet). The domain isn't editable until a model
    // is chosen; changing the sibling re-resolves on the next render.
    if (!model) {
        return (
            <div className="domain-selector-field is-disabled" aria-disabled="true">
                <span className="dsf__icon"><Filter size={16} /></span>
                <span className="dsf__summary muted">Select a model to build a domain</span>
            </div>
        );
    }

    return (
        <>
            <button type="button" className="domain-selector-field" onClick={() => setOpen(true)}>
                <span className="dsf__icon"><Filter size={16} /></span>
                <span className="dsf__summary">{summaryText(domain)} <span className="muted">on {model}</span></span>
                <span className="dsf__edit"><EditIcon size={13} /> Edit domain</span>
            </button>
            {open ? (
                <DomainBuilderDialog
                    key={model}
                    open={open}
                    model={model}
                    spec={spec}
                    loadSpec={loadSpec}
                    countFor={countFor ? (d) => countFor(model, d) : undefined}
                    loadOptions={loadOptions}
                    value={domain}
                    onApply={handleApply}
                    onClose={() => setOpen(false)}
                />
            ) : null}
        </>
    );
}

/* ---- runtime host (mounted by FormSheet) ------------------------------ */

export type DomainSelectorEditorHostProps = Omit<DomainSelectorEditorProps, "loadSpec" | "spec" | "countFor" | "loadOptions">;

/**
 * Form-host wrapper that wires the live introspection + count reads from the
 * app runtime, then delegates to the pure `DomainSelectorEditor`. Kept separate
 * so the editor itself stays runtime-free and unit-testable (tests render
 * `DomainSelectorEditor` directly with an injected `spec` / `loadSpec`).
 */
export function DomainSelectorEditorHost(props: DomainSelectorEditorHostProps) {
    const runtime = useRuntime();
    const action = runtime.services.action;

    const loadSpec: SpecLoader = useMemo(
        () => (modelKey: string): Promise<ModelSpec | null> => action.modelFields(modelKey),
        [action],
    );
    const countFor = useMemo(
        () => (model: string, domain: Domain): Promise<number | null> => action.countRecords(model, domain),
        [action],
    );
    const loadOptions = useMemo(
        () => (relModel: string): Promise<Array<[string, string]>> => action.pickerOptions(relModel),
        [action],
    );

    return <DomainSelectorEditor {...props} loadSpec={loadSpec} countFor={countFor} loadOptions={loadOptions} />;
}
